import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import axios from 'axios';
import {
  Send, Loader2, CheckCircle, AlertCircle, Upload, X, Mail,
  FileAudio, ChevronLeft, ChevronRight, Eye, Check,
  XCircle, Clock, Music, Disc3, Sparkles, RotateCcw, Play, Pause,
  Headphones, BookOpen,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { authSelector } from '../../features/auth/slice';

const PUBLIC_BASE = (() => {
  const h = window.location.hostname;
  return h === 'impink.co.kr' || h === 'www.impink.co.kr' ||
    h === 'urpink.co.kr' || h === 'www.urpink.co.kr' ||
    h === 'localhost' || h === '100.68.115.13' || h === '100.116.73.97' ? '' : '/public';
})();

const ALBUM_TYPES = [
  { value: 'single', label: '싱글' },
  { value: 'ep', label: 'EP' },
  { value: 'regular', label: '정규' },
  { value: 'other', label: '기타' },
];

const BEAT_OPTIONS = [
  { value: 'purchased', label: '구매' },
  { value: 'not_purchased', label: '구매하지 않음' },
];

const GENRES = [
  '발라드', '댄스', '랩/힙합', '알앤비/소울', '록/메탈',
  '포크/블루스', '일렉트로니카', '뉴에이지', '재즈', '국악', '얼터너티브',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getWeekRange(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diffToWed = (day - 3 + 7) % 7;
  const wedStart = new Date(d);
  wedStart.setDate(d.getDate() - diffToWed);
  const tueEnd = new Date(wedStart);
  tueEnd.setDate(wedStart.getDate() + 6);
  return { start: wedStart, end: tueEnd };
}

function formatDate(d) {
  const date = new Date(d);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
}

const STATUS_MAP = {
  pending: { label: '검토중', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', icon: Clock },
  approved: { label: '발매', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', icon: Check },
  rejected: { label: '거절', color: 'text-rose-400 bg-rose-500/10 border-rose-500/20', icon: XCircle },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}>
      <Icon size={12} />
      {s.label}
    </span>
  );
}

// ── 작성 폼 컴포넌트 ──
function ProposalForm({ onSuccess }) {
  const { me } = useSelector(authSelector.selectAuthState);
  const fileInputRef = useRef(null);

  const getInitialPhone = () => {
    if (!me) return '';
    const { phone1, phone2, phone3 } = me;
    if (phone1 && phone2 && phone3) return `${phone1}-${phone2}-${phone3}`;
    return '';
  };
  const getInitialEmail = () => {
    if (!me) return '';
    const { emailId, emailDomain } = me;
    if (emailId && emailDomain) return `${emailId}@${emailDomain}`;
    return '';
  };

  const [form, setForm] = useState({
    artistName: '', albumType: 'single', demoStatus: '', beatPurchase: 'not_purchased',
    genre: '', referenceFiles: '', releaseTimeline: '', introduction: '',
    referenceLinks: '', phone: getInitialPhone(), email: getInitialEmail(),
  });
  const [demoFile, setDemoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
    let formatted = raw;
    if (raw.length > 3 && raw.length <= 7) formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
    else if (raw.length > 7) formatted = `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7)}`;
    handleChange('phone', formatted);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      return setResult({ type: 'error', message: 'MP3 파일만 첨부할 수 있습니다.' });
    }
    if (file.size > MAX_FILE_SIZE) {
      return setResult({ type: 'error', message: '파일 크기는 10MB 이하만 가능합니다.' });
    }
    setResult(null);
    setDemoFile(file);
  };

  const handleFileChange = (e) => validateAndSetFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  const handleSubmit = async () => {
    const required = [
      ['artistName', '아티스트명'], ['demoStatus', '데모 음원 진행 상황'],
      ['genre', '장르'], ['releaseTimeline', '원하는 발매 시기'],
      ['introduction', '아티스트 및 앨범 소개'], ['referenceLinks', '참고 링크'],
      ['phone', '휴대폰 번호'], ['email', '이메일'],
    ];
    for (const [key, label] of required) {
      if (!form[key]?.trim()) return setResult({ type: 'error', message: `${label}을(를) 입력해 주세요.` });
    }
    if (!/^\d{3}-\d{3,4}-\d{4}$/.test(form.phone)) return setResult({ type: 'error', message: '휴대폰 번호 형식이 올바르지 않습니다. (예: 010-1234-5678)' });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setResult({ type: 'error', message: '이메일 형식이 올바르지 않습니다.' });
    if (!demoFile) return setResult({ type: 'error', message: '데모 음원 파일을 첨부해 주세요.' });

    setSubmitting(true);
    setResult(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('demoFile', demoFile);
      await axios.post('/api/distribution-proposal', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult({ type: 'success', message: '유통제안이 성공적으로 접수되었습니다.' });
      setForm({
        artistName: '', albumType: 'single', demoStatus: '', beatPurchase: 'not_purchased',
        genre: '', referenceFiles: '', releaseTimeline: '', introduction: '',
        referenceLinks: '', phone: '', email: '',
      });
      setDemoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onSuccess?.();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.errorMessage || '제출에 실패했습니다.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-2.5 bg-[#0d1526] border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-sky-500/50 transition-colors';
  const labelClass = 'flex items-center gap-1.5 text-slate-400 text-xs font-medium mb-2';

  return (
    <div className="rounded-2xl bg-gradient-to-r from-sky-600 to-violet-600 p-[1px]">
      <div className="rounded-2xl bg-[#0f1a2e] px-5 sm:px-10 py-8 sm:py-10">
        <h2 className="text-white text-xl sm:text-2xl font-bold mb-2 flex items-center gap-3">
          <Send size={22} className="text-sky-400" />
          유통제안 작성
        </h2>
        <p className="text-slate-500 text-sm mb-8">
          모든 항목을 빠짐없이 작성해 주세요. 제출된 제안은 영업일 기준 1주일 이내에 검토됩니다.
        </p>

        {/* 아티스트명 */}
        <div className="mb-4">
          <label className={labelClass}>아티스트명 <span className="text-rose-400">*</span></label>
          <input type="text" value={form.artistName} onChange={(e) => handleChange('artistName', e.target.value)} placeholder="아티스트명을 입력하세요" className={inputClass} />
        </div>

        {/* 앨범형태 */}
        <div className="mb-4">
          <label className={labelClass}>앨범형태 <span className="text-rose-400">*</span></label>
          <div className="flex flex-wrap gap-3">
            {ALBUM_TYPES.map((t) => (
              <label key={t.value} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${form.albumType === t.value ? 'bg-sky-500/10 border-sky-500/40 text-sky-400' : 'bg-[#0d1526] border-white/10 text-slate-400 hover:border-white/20'}`}>
                <input type="radio" name="albumType" value={t.value} checked={form.albumType === t.value} onChange={(e) => handleChange('albumType', e.target.value)} className="sr-only" />
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.albumType === t.value ? 'border-sky-400' : 'border-slate-600'}`}>
                  {form.albumType === t.value && <div className="w-2 h-2 rounded-full bg-sky-400" />}
                </div>
                <span className="text-sm">{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 데모 음원 진행 상황 */}
        <div className="mb-4">
          <label className={labelClass}>데모 음원 진행 상황 <span className="text-rose-400">*</span></label>
          <p className="text-slate-600 text-xs mb-2">가이드, 데모, 가믹스, 마스터 등 첨부된 음원의 진행 상황을 기재해주세요.</p>
          <input type="text" value={form.demoStatus} onChange={(e) => handleChange('demoStatus', e.target.value)} placeholder="예) 마스터링 완료" className={inputClass} />
        </div>

        {/* 비트 구매 여부 */}
        <div className="mb-4">
          <label className={labelClass}>비트 구매 여부 <span className="text-rose-400">*</span></label>
          <p className="text-slate-600 text-xs mb-2">타입비트를 사용했거나, 비트메이커에게 비트를 구매한 경우 체크 부탁드립니다. (1:1 작업 제외)</p>
          <div className="flex flex-wrap gap-3">
            {BEAT_OPTIONS.map((t) => (
              <label key={t.value} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${form.beatPurchase === t.value ? 'bg-sky-500/10 border-sky-500/40 text-sky-400' : 'bg-[#0d1526] border-white/10 text-slate-400 hover:border-white/20'}`}>
                <input type="radio" name="beatPurchase" value={t.value} checked={form.beatPurchase === t.value} onChange={(e) => handleChange('beatPurchase', e.target.value)} className="sr-only" />
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.beatPurchase === t.value ? 'border-sky-400' : 'border-slate-600'}`}>
                  {form.beatPurchase === t.value && <div className="w-2 h-2 rounded-full bg-sky-400" />}
                </div>
                <span className="text-sm">{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 장르 */}
        <div className="mb-4">
          <label className={labelClass}>장르 <span className="text-rose-400">*</span></label>
          <div className="flex flex-wrap gap-2">
            {GENRES.map((g) => (
              <label key={g} className={`px-3.5 py-2 rounded-xl border cursor-pointer transition-colors text-sm ${form.genre === g ? 'bg-sky-500/10 border-sky-500/40 text-sky-400' : 'bg-[#0d1526] border-white/10 text-slate-400 hover:border-white/20'}`}>
                <input type="radio" name="genre" value={g} checked={form.genre === g} onChange={(e) => handleChange('genre', e.target.value)} className="sr-only" />
                {g}
              </label>
            ))}
          </div>
        </div>

        {/* 데모 음원 파일 */}
        <div className="mb-4">
          <label className={labelClass}><FileAudio size={13} /> 데모음원 <span className="text-rose-400">*</span> <span className="text-slate-600">(MP3, 최대 10MB)</span></label>
          {!demoFile ? (
            <button type="button" onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-6 bg-[#0d1526] border-2 border-dashed rounded-xl transition-colors cursor-pointer ${dragOver ? 'border-sky-500 bg-sky-500/5 text-sky-400' : 'border-white/10 hover:border-sky-500/40 text-slate-500 hover:text-slate-300'}`}>
              <Upload size={20} />
              <span className="text-sm">{dragOver ? '여기에 놓으세요' : 'MP3 파일을 드래그하거나 클릭하여 선택하세요'}</span>
            </button>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0"><FileAudio size={18} className="text-emerald-400" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{demoFile.name}</p>
                <p className="text-slate-500 text-xs">{formatFileSize(demoFile.size)}</p>
              </div>
              <button type="button" onClick={() => { setDemoFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500 hover:text-rose-400 transition-colors"><X size={16} /></button>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept=".mp3,audio/mpeg" onChange={handleFileChange} className="hidden" />
        </div>

        {/* 기타 참고자료 */}
        <div className="mb-4">
          <label className={labelClass}>기타 참고자료</label>
          <p className="text-slate-600 text-xs mb-2">파일 다운로드 링크로 전달하는 경우 입력해주세요. (구글 드라이브 전달 시 권한 확인 바랍니다.)</p>
          <input type="text" value={form.referenceFiles} onChange={(e) => handleChange('referenceFiles', e.target.value)} placeholder="https://" className={inputClass} />
        </div>

        {/* 원하는 발매 시기 */}
        <div className="mb-4">
          <label className={labelClass}>원하는 발매 시기 <span className="text-rose-400">*</span></label>
          <input type="text" value={form.releaseTimeline} onChange={(e) => handleChange('releaseTimeline', e.target.value)} placeholder="예) 가능한 빨리, 4월 초, 5월 중순 등" className={inputClass} />
        </div>

        {/* 아티스트 및 앨범 소개 */}
        <div className="mb-4">
          <label className={labelClass}>아티스트 및 앨범 소개 <span className="text-rose-400">*</span></label>
          <p className="text-slate-600 text-xs mb-2">그 동안의 발매/참여, 공연, 방송, 대회 이력과 함께 발매 예정 앨범의 주제, 제작 과정, 참여진 등을 기재해주세요.</p>
          <textarea value={form.introduction} onChange={(e) => handleChange('introduction', e.target.value)} rows={5} placeholder="아티스트와 앨범에 대해 소개해 주세요" className={inputClass + ' resize-none'} />
        </div>

        {/* 참고 링크 */}
        <div className="mb-4">
          <label className={labelClass}>참고 링크 <span className="text-rose-400">*</span></label>
          <p className="text-slate-600 text-xs mb-2">활동 이력을 참고할 수 있는 음원사이트, 아티스트 공식 SNS 등의 링크를 기재해주세요. (신인의 경우에는 "신인"이라고 적어주세요.)</p>
          <input type="text" value={form.referenceLinks} onChange={(e) => handleChange('referenceLinks', e.target.value)} placeholder="https:// 또는 신인" className={inputClass} />
        </div>

        {/* 연락처 */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className={labelClass}>휴대폰 번호 <span className="text-rose-400">*</span></label>
            <input type="tel" value={form.phone} onChange={handlePhoneChange} placeholder="010-1234-5678" maxLength={13} inputMode="numeric" pattern="[0-9-]*" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>이메일 <span className="text-rose-400">*</span></label>
            <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="example@email.com" inputMode="email" className={inputClass} />
          </div>
        </div>

        {/* 결과 메시지 */}
        {result && (
          <div className={`flex items-start gap-3 rounded-xl px-5 py-4 mb-6 text-sm ${result.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'}`}>
            {result.type === 'success' ? <CheckCircle size={18} className="shrink-0 mt-0.5" /> : <AlertCircle size={18} className="shrink-0 mt-0.5" />}
            <p>{result.message}</p>
          </div>
        )}

        {/* 제출 버튼 */}
        <button onClick={handleSubmit} disabled={submitting} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-400 hover:to-violet-400 disabled:from-slate-600 disabled:to-slate-600 rounded-xl text-white font-semibold text-sm transition-all shadow-lg shadow-sky-500/25 disabled:shadow-none disabled:cursor-not-allowed">
          {submitting ? (<><Loader2 size={16} className="animate-spin" />제출 중...</>) : (<><Send size={16} />유통제안 제출</>)}
        </button>
      </div>
    </div>
  );
}

// ── 주간 네비게이션 ──
function WeekNav({ weekStart, onWeekChange }) {
  const week = getWeekRange(weekStart);
  const currentWeek = getWeekRange();
  const isCurrentWeek = week.start.getTime() === currentWeek.start.getTime();

  return (
    <div className="flex items-center justify-between mb-6">
      <button onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); onWeekChange(d); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
        <ChevronLeft size={20} />
      </button>
      <div className="text-center">
        <p className="text-white font-semibold text-sm sm:text-base">
          {formatDate(week.start)} ~ {formatDate(week.end)}
        </p>
        {isCurrentWeek && <p className="text-sky-400 text-xs mt-0.5">이번 주</p>}
      </div>
      <button onClick={() => { if (!isCurrentWeek) { const d = new Date(weekStart); d.setDate(d.getDate() + 7); onWeekChange(d); } }} disabled={isCurrentWeek} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
        <ChevronRight size={20} />
      </button>
    </div>
  );
}

// ── 관리자 처리 모달 ──
function RejectModal({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md bg-[#131e33] rounded-2xl border border-white/10 p-6">
        <h3 className="text-white font-bold text-lg mb-4">거절 사유</h3>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="내부 검토 결과 한정된 발매 수량에 의하여 이번 제안에는 아쉽게도 함께 하지 못하게 되었음을 알려 드립니다. 감사합니다." className="w-full px-4 py-3 bg-[#0d1526] border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-rose-500/50 resize-none mb-4" />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/5 text-slate-400 hover:text-white text-sm transition-colors">취소</button>
          <button onClick={() => { onConfirm(reason.trim()); onClose(); setReason(''); }} className="px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-400 text-white text-sm font-medium transition-colors">거절 확인</button>
        </div>
      </div>
    </div>
  );
}

// ── 메일 미리보기 / 발송 모달 ──
function EmailPreviewModal({ open, onClose, proposals, onSendComplete, resendMode }) {
  const [previewType, setPreviewType] = useState('approved');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const sendable = proposals.filter((p) => p.status === 'approved' || p.status === 'rejected');
  const targets = resendMode ? sendable : sendable.filter((p) => !p.emailSentAt);
  const approvedCount = targets.filter((p) => p.status === 'approved').length;
  const rejectedCount = targets.filter((p) => p.status === 'rejected').length;

  useEffect(() => {
    if (!open) return;
    setResult(null);
    axios.get('/api/distribution-proposal/email-preview', { params: { type: previewType } })
      .then(({ data }) => { setPreviewHtml(data.html); setPreviewSubject(data.subject); })
      .catch(() => { setPreviewHtml('<p>미리보기를 불러올 수 없습니다.</p>'); setPreviewSubject(''); });
  }, [open, previewType]);

  const handleSend = async () => {
    if (targets.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post('/api/distribution-proposal/send-result-emails', {
        proposalIds: targets.map((p) => p.id),
      });
      setResult({ type: 'success', message: data.message, detail: data });
      onSendComplete?.();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.errorMessage || '발송에 실패했습니다.' });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 overflow-y-auto py-8">
      <div className="w-full max-w-2xl bg-[#131e33] rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg flex items-center gap-2"><Mail size={20} className="text-sky-400" /> 결과 메일 발송</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        {/* 발송 대상 요약 */}
        <div className="flex gap-3 mb-5">
          <div className="flex-1 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
            <p className="text-emerald-400 text-xs font-medium mb-1">발매 승인</p>
            <p className="text-white text-xl font-bold">{approvedCount}<span className="text-slate-500 text-sm font-normal ml-1">건</span></p>
          </div>
          <div className="flex-1 px-4 py-3 rounded-xl bg-rose-500/5 border border-rose-500/20">
            <p className="text-rose-400 text-xs font-medium mb-1">거절</p>
            <p className="text-white text-xl font-bold">{rejectedCount}<span className="text-slate-500 text-sm font-normal ml-1">건</span></p>
          </div>
        </div>

        {/* 미리보기 탭 */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setPreviewType('approved')} className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${previewType === 'approved' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-slate-400 border border-transparent hover:text-white'}`}>
            승인 메일 미리보기
          </button>
          <button onClick={() => setPreviewType('rejected')} className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${previewType === 'rejected' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' : 'bg-white/5 text-slate-400 border border-transparent hover:text-white'}`}>
            거절 메일 미리보기
          </button>
        </div>

        {/* 제목 + HTML 미리보기 */}
        {previewSubject && (
          <div className="flex items-center gap-2 px-4 py-2.5 mb-2 rounded-xl bg-white/5 border border-white/10">
            <span className="text-slate-500 text-xs shrink-0">제목</span>
            <span className="text-white text-sm font-medium truncate">{previewSubject}</span>
          </div>
        )}
        <div className="rounded-xl border border-white/10 overflow-hidden mb-5 bg-white" style={{ maxHeight: 400, overflowY: 'auto' }}>
          <iframe
            srcDoc={previewHtml}
            title="email-preview"
            className="w-full border-0"
            style={{ height: 380 }}
          />
        </div>

        {/* 발송 대상 목록 */}
        {targets.length > 0 && (
          <div className="mb-5 max-h-32 overflow-y-auto">
            <p className="text-slate-500 text-xs mb-2">{resendMode ? '재발송' : '발송'} 대상 ({targets.length}건)</p>
            <div className="space-y-1">
              {targets.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-white/5">
                  <StatusBadge status={p.status} />
                  <span className="text-white">{p.artistName}</span>
                  <span className="text-slate-500">→ {p.email}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 결과 메시지 */}
        {result && (
          <div className={`px-4 py-3 rounded-xl mb-4 text-sm ${result.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'}`}>
            {result.message}
          </div>
        )}

        {/* 발송 버튼 */}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-white text-sm transition-colors">닫기</button>
          <button
            onClick={handleSend}
            disabled={sending || targets.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-400 hover:to-violet-400 text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? <><Loader2 size={14} className="animate-spin" /> 발송중...</> : <><Mail size={14} /> {targets.length}건 {resendMode ? '재발송' : '메일 발송'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 페이지 네비게이션 (작성자용) ──
function PageNav({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
        <ChevronLeft size={18} />
      </button>
      <span className="text-slate-400 text-sm px-3">{page} / {totalPages}</span>
      <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

// ── 제안 목록 (회원 + 관리자 공용) ──
function ProposalList({ isAdminUser, refreshKey }) {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => getWeekRange().start);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [playingId, setPlayingId] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailResendMode, setEmailResendMode] = useState(false);
  const audioRef = useRef(null);
  const isCurrentWeek = getWeekRange().start.getTime() === getWeekRange(weekStart).start.getTime();

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const params = isAdminUser
        ? { weekStart: `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}` }
        : { page, limit: 10 };
      const { data } = await axios.get('/api/distribution-proposal', { params });
      setProposals(data.proposals || []);
      if (!isAdminUser) setTotalPages(data.totalPages || 1);
    } catch {
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [isAdminUser, weekStart, page]);

  useEffect(() => { fetchProposals(); }, [fetchProposals, refreshKey]);

  const handleProcess = async (id, action, rejectReason) => {
    setProcessing(id);
    try {
      await axios.put(`/api/distribution-proposal/${id}/process`, { action, rejectReason });
      fetchProposals();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '처리에 실패했습니다.');
    } finally {
      setProcessing(null);
    }
  };

  const togglePlay = (id, url) => {
    if (playingId === id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.onended = () => setPlayingId(null);
      audio.play();
      audioRef.current = audio;
      setPlayingId(id);
    }
  };

  return (
    <div className="rounded-2xl bg-[#131e33] border border-white/10 p-5 sm:p-8">
      <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-3">
        <Disc3 size={20} className="text-violet-400" />
        {isAdminUser ? '주간 유통제안 목록' : '내 유통제안'}
      </h2>

      {isAdminUser && <WeekNav weekStart={weekStart} onWeekChange={setWeekStart} />}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : proposals.length === 0 ? (
        <div className="text-center py-12 text-slate-600">
          <Music size={32} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">아직 접수된 제안이 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => (
            <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 bg-[#0d1526] rounded-xl border border-white/5 hover:border-white/10 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white font-medium text-sm truncate">{p.artistName}</p>
                  <span className="text-slate-600 text-xs">{ALBUM_TYPES.find((t) => t.value === p.albumType)?.label}</span>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-slate-500 text-xs">
                  {p.genre} · {formatDate(p.createdAt)}
                  {isAdminUser && (p.member?.name || p.client?.name) && <span className="ml-2 text-slate-600">({p.member?.name || p.client?.name})</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isAdminUser && p.demoFileUrl && (
                  <button onClick={() => togglePlay(p.id, p.demoFileUrl)} className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${playingId === p.id ? 'bg-sky-500 text-white' : 'bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white'}`}>
                    {playingId === p.id ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                )}
                <button onClick={() => navigate(`${PUBLIC_BASE}/proposal/${p.id}`)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-xs transition-colors">
                  <Eye size={14} /> 상세
                </button>
                {isAdminUser && (
                  <>
                    {p.status !== 'approved' && (
                      <button disabled={processing === p.id} onClick={() => handleProcess(p.id, 'approved')} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium transition-colors disabled:opacity-50">
                        <Check size={14} /> 발매
                      </button>
                    )}
                    {p.status !== 'rejected' && (
                      <button disabled={processing === p.id} onClick={() => setRejectTarget(p.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-medium transition-colors disabled:opacity-50">
                        <XCircle size={14} /> 거절
                      </button>
                    )}
                    {p.status !== 'pending' && (
                      <button disabled={processing === p.id} onClick={() => handleProcess(p.id, 'pending')} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-medium transition-colors disabled:opacity-50">
                        <RotateCcw size={14} /> 검토중
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isAdminUser && <PageNav page={page} totalPages={totalPages} onPageChange={setPage} />}

      {isAdminUser && proposals.length > 0 && proposals.some((p) => p.status === 'approved' || p.status === 'rejected') && (() => {
        const processed = proposals.filter((p) => p.status === 'approved' || p.status === 'rejected');
        const unsent = processed.filter((p) => !p.emailSentAt);
        const allSent = unsent.length === 0;
        return (
          <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
            <p className="text-slate-500 text-xs">
              {allSent
                ? <span className="text-emerald-500/60">{processed.length}건 발송 완료</span>
                : <>처리 완료된 제안에 결과 메일을 발송합니다.
                    {proposals.some((p) => p.emailSentAt) && (
                      <span className="ml-2 text-emerald-500/60">({proposals.filter((p) => p.emailSentAt).length}건 발송 완료)</span>
                    )}
                  </>
              }
            </p>
            <button
              onClick={() => {
                if (allSent) {
                  if (window.confirm('이미 발송 완료된 메일을 재발송하시겠습니까?')) {
                    setEmailResendMode(true);
                    setEmailModalOpen(true);
                  }
                } else {
                  setEmailResendMode(false);
                  setEmailModalOpen(true);
                }
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${allSent ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20' : 'bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-400 hover:to-violet-400 text-white'}`}
            >
              <Mail size={15} /> {allSent ? '발송완료' : '결과 메일 발송'}
            </button>
          </div>
        );
      })()}

      <RejectModal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={(reason) => handleProcess(rejectTarget, 'rejected', reason)}
      />
      <EmailPreviewModal
        open={emailModalOpen}
        onClose={() => { setEmailModalOpen(false); setEmailResendMode(false); }}
        proposals={proposals}
        onSendComplete={() => fetchProposals()}
        resendMode={emailResendMode}
      />
    </div>
  );
}

// ── 메인 페이지 ──
function PublicProposalPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  const isMember = me?.userType === 'member';
  const isClient = me?.userType === 'client';
  const isAdminUser = me?.adminYn === 'Y';

  useEffect(() => {
    if (!me) {
      navigate(`${PUBLIC_BASE}/login`, { state: { from: `${PUBLIC_BASE}/proposal` } });
    }
  }, [me, navigate]);

  if (!me) return null;

  return (
    <PublicLayout>
      <SEO title="유통제안" description="p!nk에 음원 유통을 제안하세요." path="/proposal" />

      {/* 히어로 */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 via-violet-600/10 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.15),transparent)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-10 sm:pt-20 sm:pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-xs font-medium mb-6">
            <Sparkles size={13} /> Distribution Proposal
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">유통문의</h1>
          <p className="text-slate-400 text-sm sm:text-base">
            {isAdminUser ? '접수된 유통제안을 검토하고 처리하세요.' : '당신의 음악을 p!nk와 함께 세상에 전하세요.'}
          </p>
          {/* 탭 */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <Link
              to={`${PUBLIC_BASE}/inquiry`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]">
              <BookOpen size={14} />
              유통안내
            </Link>
            <Link
              to={`${PUBLIC_BASE}/proposal`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-sky-500/20 text-sky-400 border border-sky-500/30">
              <Send size={14} />
              유통제안
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-16 space-y-10">
        {/* 관리자: 주간 목록 */}
        {isAdminUser && <ProposalList isAdminUser refreshKey={refreshKey} />}

        {/* 비관리자: 작성 폼 + 내 제안 목록 */}
        {!isAdminUser && (
          <>
            <ProposalForm onSuccess={() => setRefreshKey((k) => k + 1)} />
            <ProposalList isAdminUser={false} refreshKey={refreshKey} />
          </>
        )}
      </div>
    </PublicLayout>
  );
}

export default PublicProposalPage;
